// app/javascript/components/quotes/builder/components/SummaryPane.js
import React, { useMemo } from "react"
import { useQuoteEditor } from "../context/QuoteEditorContext"
import { nodeTotals, isOption, optionsTotal, countBy, collectByCostCategory, collectVatBreakdown } from "../helpers/treeMath"
import { formatNumber, roundNumber } from "../helpers/formatting"
import { roundMoney } from "@react/helpers/helpers"
import Label from "@react/shared/ui/Label"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

const ROW     = "flex justify-between items-baseline"
const SECTION = "p-4 border-b border-slate-200 last:border-b-0"
const SEP     = "h-px bg-slate-200 my-1"

const SummaryPane = () => {
  const { state } = useQuoteEditor()
  const activeSections = (state.sections || []).filter(s => !s._destroy)
  const isPreview      = state.activeTab === "preview"
  // Without the margin right the cost figures go everywhere, not only in the
  // client preview.
  const hideMargins    = isPreview || !state.showMargin

  const symbol = state.currencySymbol || "€"
  const discount = parseFloat(state.discount) || 0

  const { subtotalHT, subtotalCost, vatBreakdown, perCategory, labourHours } = useMemo(() => {
    const active = (state.sections || []).filter(s => !s._destroy)
    const sectionTotals = active.filter(s => !isOption(s)).map(s => nodeTotals(s))
    const subtotalHT   = roundNumber(sectionTotals.reduce((s, t) => s + t.total, 0))
    const subtotalCost = roundNumber(sectionTotals.reduce((s, t) => s + t.cost, 0))
    const vats = {}
    active.forEach(s => collectVatBreakdown(s, vats))

    const merged = {}
    active.forEach(section => {
      const buckets = collectByCostCategory(section)
      Object.entries(buckets).forEach(([cost_category, m]) => {
        const e = (merged[cost_category] ||= { sale: 0, cost: 0, hours: 0, isLabour: m.isLabour, name: m.name })
        e.sale  += m.sale
        e.cost  += m.cost
        e.hours += m.hours
      })
    })
    const perCategory = Object.values(merged)
      .map(m => {
        const sale   = roundNumber(m.sale)
        const cost   = roundNumber(m.cost)
        const margin = roundNumber(sale - cost)
        return {
          name:      m.name,
          sale,
          cost,
          margin,
          coef:      cost > 0 ? sale / cost : null,
          marginPct: sale > 0 ? (margin / sale) * 100 : 0,
          hours:     m.isLabour ? roundNumber(m.hours) : null,
        }
      })
      .sort((a, b) => b.sale - a.sale)

    const labourHours = roundNumber(perCategory.reduce((sum, c) => sum + (c.hours || 0), 0))

    return { subtotalHT, subtotalCost, vatBreakdown: vats, perCategory, labourHours }
  }, [state.sections])

  const discountAmount = roundNumber(subtotalHT * discount / 100)
  const totalHT        = roundNumber(subtotalHT - discountAmount)

  const vatAmounts = {}
  let totalVAT = 0
  Object.entries(vatBreakdown).forEach(([pct, base]) => {
    const discounted = base * (1 - discount / 100)
    const amount     = roundMoney(parseFloat(pct) * discounted / 100)
    vatAmounts[pct]  = amount
    totalVAT += amount
  })
  totalVAT = roundNumber(totalVAT)
  const totalTTC = roundNumber(totalHT + totalVAT)
  const optionsHT = roundNumber(activeSections.reduce((sum, s) => sum + optionsTotal(s), 0))

  const marginAmount = roundNumber(subtotalHT - subtotalCost)
  const marginPct    = subtotalHT > 0 ? roundNumber(marginAmount / subtotalHT * 100) : 0
  const avgCoef      = subtotalCost > 0 ? subtotalHT / subtotalCost : 1

  const sectionCount = activeSections.length
  const groupCount   = activeSections.reduce((n, s) => n + countBy(s, "group"), 0)
  const lineCount    = activeSections.reduce((n, s) => n + countBy(s, "service"), 0)

  const ringPct  = Math.max(0, Math.min(100, marginPct))
  const ringStyle = {
    background: `conic-gradient(#16A34A 0% ${ringPct}%, #F1F5F9 ${ringPct}% 100%)`,
  }

  const k = "text-slate-500"
  const v = "tabular-nums text-slate-700 font-medium"

  return (
    <>
      {/* ─── Totals ─────────────────────────────────────────── */}
      <div className={SECTION}>
        <div className="flex flex-col gap-[7px] text-[13px] tabular-nums">
          {Object.entries(vatBreakdown)
            .sort((a, b) => parseFloat(a[0]) - parseFloat(b[0]))
            .map(([pct, base]) => {
              const discountedBase = roundMoney(base * (1 - discount / 100))
              const amount = vatAmounts[pct] || 0
              return (
                <React.Fragment key={pct}>
                  <div className={`${ROW} text-[12px]`}>
                    <span className="text-slate-400">{I18n.t("quotes.builder.summary.vat_base", { pct: pct })}</span>
                    <span className="tabular-nums text-slate-500 font-medium">{formatNumber(discountedBase)} {symbol}</span>
                  </div>
                  <div className={ROW}>
                    <span className={k}>{I18n.t("quotes.builder.summary.vat_line", { pct: pct })}</span>
                    <span className={v}>{formatNumber(amount)} {symbol}</span>
                  </div>
                </React.Fragment>
              )
            })}
          {Object.keys(vatBreakdown).length > 0 && <div className={SEP} />}

          <div className={ROW}><span className={k}>{I18n.t("quotes.builder.summary.subtotal_excl_tax")}</span><span className={v}>{formatNumber(subtotalHT)} {symbol}</span></div>
          {discountAmount > 0 && (
            <div className={ROW}>
              <span className={k}>{I18n.t("quotes.builder.summary.discount", { discount: discount })}</span>
              <span className="tabular-nums text-emerald-600 font-medium">− {formatNumber(discountAmount)} {symbol}</span>
            </div>
          )}
          <div className={ROW}><span className={k}>{I18n.t("quotes.builder.summary.vat")}</span><span className={v}>{formatNumber(totalVAT)} {symbol}</span></div>
          <div className={SEP} />
          <div className={`${ROW} pt-1.5 pb-0.5`}>
            <span className="text-[13px] text-slate-900 font-semibold">{I18n.t("quotes.builder.summary.total_incl_tax")}</span>
            <span className="tabular-nums text-slate-900 font-bold text-[20px] -tracking-[0.015em]">{formatNumber(totalTTC)} {symbol}</span>
          </div>
          {/* The hours the quote sells, read like the total: the site's budget
              of labour, which the worksite will follow hour by hour. */}
          {labourHours > 0 && (
            <div className={`${ROW} mt-1 rounded-md bg-slate-100 px-2.5 py-2`}>
              <span className="text-[13px] text-slate-700 font-semibold inline-flex items-center gap-1.5">
                <FontAwesomeIcon icon="helmet-safety" className="text-slate-400" />
                {I18n.t("quotes.builder.summary.labor_hours")}
              </span>
              <span className="tabular-nums text-slate-900 font-bold text-[17px] -tracking-[0.015em]">{formatNumber(labourHours)} h</span>
            </div>
          )}
          {optionsHT > 0 && (
            <div className={`${ROW} mt-1 rounded-md bg-amber-50 px-2 py-1.5`}>
              <span className="text-amber-800 font-medium">{I18n.t("quotes.builder.summary.options_excluded")}</span>
              <span className="tabular-nums italic text-slate-500 font-semibold">{formatNumber(optionsHT)} {symbol}</span>
            </div>
          )}
        </div>
      </div>

      {/* ─── Marge ──────────────────────────────────────────── */}
      {!hideMargins && (
        <div className={SECTION}>
          <Label size="md" className="text-slate-500 mb-3 block">{I18n.t("quotes.builder.summary.margin")}</Label>
          <div className="flex items-center gap-4">
            <div
              className="relative flex items-center justify-center flex-none rounded-full before:content-[''] before:absolute before:inset-[9px] before:bg-white before:rounded-full"
              style={{ width: 88, height: 88, ...ringStyle }}
            >
              <span className="relative text-[17px] font-bold -tracking-[0.015em] text-slate-900">
                {Math.round(marginPct)}<span className="text-[11px] text-slate-400 font-medium">%</span>
              </span>
            </div>
            <div className="flex-1 text-[12.5px] text-slate-500 leading-[1.5]">
              <strong className="block mb-0.5 text-slate-900 font-semibold text-[14px]">
                {I18n.t("quotes.builder.summary.margin_amount", { amount: `${formatNumber(marginAmount)} ${symbol}` })}
              </strong>
              {I18n.t("quotes.builder.summary.coefficient_prefix")} <strong className="font-semibold text-[13px] text-slate-700">{avgCoef.toFixed(2)}</strong>.
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2.5 mt-3">
            {[
              { n: sectionCount,                              label: I18n.t("quotes.builder.summary.groups")        },
              { n: groupCount,                                label: I18n.t("quotes.builder.summary.services")      },
              { n: lineCount,                                 label: I18n.t("quotes.builder.summary.components")    },
              { n: sectionCount + groupCount + lineCount,     label: I18n.t("quotes.builder.summary.total_lines")   },
            ].map(({ n, label }) => (
              <div key={label} className="px-2.5 py-2 bg-slate-100 rounded-lg text-[11px] text-slate-500">
                <span className="block text-[17px] font-bold text-slate-900 -tracking-[0.015em] tabular-nums">{n}</span>
                {label}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── Répartition par catégorie ─────────────────────── */}
      {perCategory.length > 0 && (
        <div className={SECTION}>
          <Label size="md" className="text-slate-500 mb-2 block">{I18n.t("quotes.builder.summary.per_category")}</Label>
          <div className={`grid gap-x-2 ${hideMargins ? "grid-cols-[minmax(0,1fr)_72px]" : "grid-cols-[minmax(0,1fr)_44px_48px_72px]"}`}>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400" />
            {!hideMargins && <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 text-right">{I18n.t("quotes.builder.summary.coef_header")}</div>}
            {!hideMargins && <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 text-right">{I18n.t("quotes.builder.summary.margin_header")}</div>}
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 text-right">{I18n.t("quotes.builder.summary.total_header")}</div>

            {perCategory.map(c => {
              const tone =
                c.cost === 0      ? "text-slate-400" :
                c.marginPct >= 25 ? "text-emerald-600" :
                c.marginPct >= 15 ? "text-amber-600"   :
                                    "text-rose-600"
              return (
                <React.Fragment key={c.name}>
                  <div className="py-1 text-[12px] font-medium text-slate-900 truncate border-t border-slate-200">
                    {c.name}
                    {c.hours > 0 && <span className="ml-1.5 text-slate-500 tabular-nums font-normal">· {formatNumber(c.hours)} h</span>}
                  </div>
                  {!hideMargins && (
                    <div className="py-1 text-[12px] text-right tabular-nums text-slate-500 border-t border-slate-200">
                      {c.coef != null ? `×${c.coef.toFixed(2)}` : "—"}
                    </div>
                  )}
                  {!hideMargins && (
                    <div className={`py-1 text-[12px] text-right tabular-nums font-semibold border-t border-slate-200 ${tone}`}>
                      {c.cost > 0 ? `${Math.round(c.marginPct)}%` : "—"}
                    </div>
                  )}
                  <div className="py-1 text-[12px] text-right tabular-nums font-medium text-slate-900 border-t border-slate-200">
                    {formatNumber(c.sale)} {symbol}
                  </div>
                </React.Fragment>
              )
            })}
          </div>
        </div>
      )}

    </>
  )
}

export default SummaryPane
